import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useApiClient } from "~/hooks/use-api-client";
import { trackEvent } from "~/lib/analytics";

// AIDEV-NOTE: Hook to dismiss a survey CTA
// Creates dismissal record so survey won't show again for this user
// surveyId is passed as mutation variable to avoid stale closure issues (EVO-XXX)

interface DismissSurveyParams {
    surveyId: string;
    surveyTitle: string;
}

export function useDismissSurvey() {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ surveyId, surveyTitle }: DismissSurveyParams) => {
            if (!surveyId) {
                throw new Error("Cannot dismiss survey: surveyId is required");
            }

            const response = await apiClient.post(
                `/surveys/${surveyId}/dismiss`,
            );

            if (!response.ok) {
                throw new Error("Failed to dismiss survey");
            }

            return response.json();
        },
        onSuccess: (_data, { surveyId, surveyTitle }) => {
            // AIDEV-NOTE: Invalidate ALL active survey queries (all paths)
            // Query key is ["surveys", "active", path] so we need to match the prefix
            queryClient.invalidateQueries({
                queryKey: ["surveys", "active"],
                exact: false, // Match all queries starting with this key
            });

            // AIDEV-NOTE: Also refresh eligible triggers since dismissal may unblock new triggers
            queryClient.invalidateQueries({
                queryKey: ["surveys", "eligible-triggers"],
            });

            // Track analytics
            trackEvent("survey_cta_dismissed", { surveyId, surveyTitle });
        },
    });
}
